import React, { useMemo, useState } from 'react';
import { useAlertesPartagees, useNotificationsPartagees } from '../context/NotificationsContext';
import NotificationItem from './NotificationItem';
import AlertItem from './AlertItem';

interface NotificationPanelProps {
    isOpen: boolean;
    onClose: () => void;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({ isOpen, onClose }) => {
    const { notifications, unreadCount: notifUnread, loading: notifLoading, markAllAsRead: markAllNotif, markAsRead, deleteMany } = useNotificationsPartagees();
    const { alerts, unreadCount: alertUnread, loading: alertLoading, markAllAsRead: markAllAlerts, dismiss, confirm } = useAlertesPartagees();

    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    const totalUnread = (notifUnread || 0) + (alertUnread || 0);

    const allSelectableIds = useMemo(() => notifications.map(n => n.id), [notifications]);
    const isAllSelected = selectedIds.length > 0 && selectedIds.length === allSelectableIds.length;

    // handleMarkAllAsRead fusionné dans le bouton global (notifications + alertes)

    const handleNotificationClick = async (notifId: number, isRead: boolean) => {
        if (!isRead) {
            try {
                await markAsRead(notifId);
            } catch (err) {
                console.error('Erreur lors du marquage de la notification:', err);
            }
        }
    };

    const toggleSelect = (id: number) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const toggleSelectAll = () => {
        if (isAllSelected) {
            setSelectedIds([]);
        } else {
            setSelectedIds(allSelectableIds);
        }
    };

    const handleDeleteSelected = async () => {
        if (selectedIds.length === 0) return;
        try {
            await deleteMany(selectedIds);
            setSelectedIds([]);
        } catch (err) {
            console.error('Suppression notifications sélectionnées échouée:', err);
        }
    };

    return (
        <>
            {/* Panneau Latéral */}
            <div
                className={`fixed top-0 right-0 h-full w-full sm:max-w-sm md:max-w-md bg-base-100 shadow-2xl z-[50] transform transition-transform duration-300 ease-in-out
                    ${isOpen ? 'translate-x-0' : 'translate-x-full'}`
                }
            >
                <div className="p-4 sm:p-6 h-full flex flex-col">
                    <div className="flex justify-between items-center pb-4 border-b border-base-300">
                        <h2 className="text-lg sm:text-xl font-bold">Notifications & Alertes ({totalUnread})</h2>
                        
                        {/* Bouton pour fermer le panneau (La Croix) */}
                        <button 
                            onClick={onClose}
                            className="btn btn-ghost btn-circle btn-sm"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Bouton Marquer toutes comme lues */}
                    {(totalUnread > 0 || notifications.length > 0) && (
                        <div className="pt-2 pb-2 flex flex-col gap-2">
                            {totalUnread > 0 && (
                                <button 
                                    onClick={async () => { await Promise.all([markAllAlerts(), markAllNotif()]); }}
                                    className="btn btn-sm btn-ghost w-full"
                                >
                                    Marquer toutes comme lues
                                </button>
                            )}
                            {notifications.length > 0 && (
                                <div className="flex items-center justify-between gap-2">
                                    <label className="flex items-center gap-2 cursor-pointer select-none">
                                        <input 
                                            type="checkbox" 
                                            className="checkbox checkbox-sm"
                                            checked={isAllSelected}
                                            onChange={toggleSelectAll}
                                        />
                                        <span className="text-sm">Tout sélectionner</span>
                                    </label>
                                    <button
                                        className="btn btn-sm btn-error"
                                        disabled={selectedIds.length === 0}
                                        onClick={handleDeleteSelected}
                                    >
                                        Supprimer sélectionnées
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Contenu des notifications */}
                    <div className="flex-1 overflow-y-auto pt-4">
                        {notifLoading || alertLoading ? (
                            <div className="flex justify-center items-center h-full">
                                <span className="loading loading-spinner loading-md"></span>
                            </div>
                        ) : (
                            <>
                                {alerts.length > 0 && (
                                    <div className="mb-3">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="text-sm font-semibold">Alertes</span>
                                            {alertUnread > 0 && <span className="badge badge-xs badge-warning">{alertUnread}</span>}
                                        </div>
                                        {alerts.map((a) => (
                                            <AlertItem
                                                key={a.id}
                                                alert={a}
                                                onDismiss={dismiss}
                                                onConfirm={confirm}
                                            />
                                        ))}
                                    </div>
                                )}
                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-sm font-semibold">Notifications</span>
                                        {notifUnread > 0 && <span className="badge badge-xs badge-warning">{notifUnread}</span>}
                                    </div>
                                    {notifications.length > 0 ? (
                                        notifications.map((notif) => (
                                            <div 
                                                key={notif.id}
                                                className="cursor-pointer flex items-start gap-2"
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="checkbox checkbox-sm mt-1"
                                                    checked={selectedIds.includes(notif.id)}
                                                    onChange={() => toggleSelect(notif.id)}
                                                />
                                                <div className="flex-1" onClick={() => handleNotificationClick(notif.id, notif.is_read)}>
                                                    <NotificationItem notif={notif} />
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-center text-base-content/70 mt-10">
                                            Aucune notification à afficher.
                                        </p>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Overlay d'arrière-plan semi-transparent */}
            {isOpen && (
                <div 
                    className="fixed inset-0 bg-black opacity-40 z-[40]"
                    onClick={onClose}
                />
            )}
        </>
    );
};

export default NotificationPanel;